import { useEffect, useState } from "react";
import { X, Play, Pause, ChevronLeft, ChevronRight, UserPlus, GraduationCap, MessageCircle, Sparkles, Trophy, Crown } from "lucide-react";

interface DemoModalProps {
  open: boolean;
  onClose: () => void;
}

const STEPS = [
  {
    icon: UserPlus,
    title: "1. Crée ton compte en 30 secondes",
    desc: "Inscris-toi avec ton email ou ton numéro. Les élèves reçoivent un identifiant KEL- unique généré automatiquement.",
    color: "from-blue-500 to-indigo-600",
  },
  {
    icon: GraduationCap,
    title: "2. Choisis ton niveau scolaire",
    desc: "Du primaire à l'université. L'IA adapte automatiquement ses explications à ta classe et ton programme.",
    color: "from-purple-500 to-pink-600",
  },
  {
    icon: MessageCircle,
    title: "3. Discute avec KelasAI",
    desc: "Pose tes questions, demande un cours, un résumé ou un exposé. L'IA répond directement, sans interruption inutile.",
    color: "from-emerald-500 to-teal-600",
  },
  {
    icon: Sparkles,
    title: "4. Vérifie ta compréhension",
    desc: "Après quelques échanges, KelasAI te propose des questions adaptées à ton niveau pour valider ce que tu as compris.",
    color: "from-amber-500 to-orange-600",
  },
  {
    icon: Trophy,
    title: "5. Progresse et gagne de l'XP",
    desc: "Gagne des niveaux, débloque des badges, affronte d'autres élèves dans les Pro Challenges et joue à des jeux éducatifs.",
    color: "from-rose-500 to-red-600",
  },
  {
    icon: Crown,
    title: "6. Passe en Premium (optionnel)",
    desc: "5 messages IA gratuits par jour. Premium = chat illimité, toutes les matières, exercices avancés et suivi parental.",
    color: "from-yellow-500 to-amber-600",
  },
];

const STEP_DURATION = 4500;

const DemoModal = ({ open, onClose }: DemoModalProps) => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!open) {
      setIndex(0);
      setProgress(0);
      setPlaying(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !playing) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - start;
      const pct = Math.min(100, (elapsed / STEP_DURATION) * 100);
      setProgress(pct);
      if (elapsed >= STEP_DURATION) {
        setIndex((i) => (i + 1) % STEPS.length);
        setProgress(0);
      } else {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, playing, index]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") { setIndex((i) => (i + 1) % STEPS.length); setProgress(0); }
      if (e.key === "ArrowLeft") { setIndex((i) => (i - 1 + STEPS.length) % STEPS.length); setProgress(0); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const step = STEPS[index];
  const Icon = step.icon;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Démo KelasAI"
    >
      <div
        className="relative w-full max-w-2xl rounded-3xl bg-card shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
          aria-label="Fermer"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>

        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 flex gap-1 p-3 z-10">
          {STEPS.map((_, i) => (
            <div key={i} className="flex-1 h-1 rounded-full bg-foreground/10 overflow-hidden">
              <div
                className="h-full bg-foreground/90 transition-all"
                style={{ width: `${i < index ? 100 : i === index ? progress : 0}%` }}
              />
            </div>
          ))}
        </div>

        {/* Visual */}
        <div className={`h-72 sm:h-80 bg-gradient-to-br ${step.color} flex items-center justify-center relative overflow-hidden`}>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle, white 1.5px, transparent 1.5px)", backgroundSize: "24px 24px" }} />
          <div
            key={index}
            className="relative animate-in zoom-in-50 fade-in duration-500"
          >
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-3xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-2xl">
              <Icon className="w-16 h-16 sm:w-20 sm:h-20 text-white" strokeWidth={1.8} />
            </div>
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-xs font-bold tracking-widest uppercase">
            Étape {index + 1} / {STEPS.length}
          </div>
        </div>

        {/* Text */}
        <div className="p-6 sm:p-8" key={`text-${index}`}>
          <div className="animate-in slide-in-from-bottom-2 fade-in duration-500">
            <h3 className="text-xl sm:text-2xl font-black text-foreground mb-2">{step.title}</h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{step.desc}</p>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            <button
              onClick={() => { setIndex((i) => (i - 1 + STEPS.length) % STEPS.length); setProgress(0); }}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Précédent"
            >
              <ChevronLeft className="w-5 h-5 text-foreground" />
            </button>

            <button
              onClick={() => setPlaying((p) => !p)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 transition-opacity"
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {playing ? "Pause" : "Lecture"}
            </button>

            <button
              onClick={() => { setIndex((i) => (i + 1) % STEPS.length); setProgress(0); }}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Suivant"
            >
              <ChevronRight className="w-5 h-5 text-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoModal;